// FCM push-notifications wrapper. Native-only (Android via Capacitor).
// Registers the device, hands the FCM token to Supabase via device_tokens table,
// and routes notification taps into the React Router history.

import { supabase } from "@/integrations/supabase/client";
import { isNativeApp } from "./platform";

let registered = false;
let lastToken: string | null = null;

async function persistTokenToSupabase(token: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  await supabase
    .from("device_tokens")
    .upsert(
      {
        user_id: user.id,
        fcm_token: token,
        platform: "android",
        last_seen_at: new Date().toISOString(),
      },
      { onConflict: "user_id,fcm_token" },
    );
}

/**
 * Request permission, register with FCM, persist the token, and wire tap
 * handlers. Safe to call multiple times — internal guard prevents re-register.
 */
export async function initializePush(opts?: {
  onNotificationTap?: (data: Record<string, unknown>) => void;
}): Promise<void> {
  if (!isNativeApp() || registered) return;
  try {
    const { PushNotifications } = await import(
      "@capacitor/push-notifications"
    );

    const perm = await PushNotifications.checkPermissions();
    let granted = perm.receive === "granted";
    if (!granted) {
      const req = await PushNotifications.requestPermissions();
      granted = req.receive === "granted";
    }
    if (!granted) return;

    await PushNotifications.addListener("registration", async (token) => {
      lastToken = token.value;
      try {
        await persistTokenToSupabase(token.value);
      } catch (err) {
        console.warn("[push] persist token failed:", err);
      }
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.warn("[push] registration error:", err);
    });

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action) => {
        opts?.onNotificationTap?.(action.notification.data ?? {});
      },
    );

    registered = true;
    await PushNotifications.register();
  } catch (err) {
    console.warn("[push] init skipped:", err);
  }
}

/** Call after sign-in so a token received before login is saved. */
export async function syncPushTokenAfterAuth(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    if (lastToken) {
      await persistTokenToSupabase(lastToken);
      return;
    }
    const { PushNotifications } = await import(
      "@capacitor/push-notifications"
    );
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") {
      const req = await PushNotifications.requestPermissions();
      if (req.receive !== "granted") return;
    }
    if (!registered) {
      await initializePush();
    } else {
      await PushNotifications.register();
    }
  } catch (err) {
    console.warn("[push] sync after auth failed:", err);
  }
}

/**
 * Remove the current device's FCM token from `device_tokens` so the user
 * stops receiving pushes after sign-out. Best-effort — never throws.
 */
export async function cleanupPushOnSignOut(): Promise<void> {
  if (!isNativeApp() || !lastToken) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase
      .from("device_tokens")
      .delete()
      .eq("user_id", user.id)
      .eq("fcm_token", lastToken);
  } catch (err) {
    console.warn("[push] cleanup failed (non-fatal):", err);
  }
}
